/**
 * MBA Lab — pure state/scoring model for the Leadership Practice Snapshot.
 * No DOM, storage, or analytics dependencies - leadership-practice-snapshot.js
 * owns all of that and consumes buildReadout() from here.
 *
 * Every item below is an original, scenario-anchored, observable-behavior
 * statement written for this tool. None of the wording, scale, or scoring is
 * copied or adapted from any licensed instrument (MSCEIT, EQ-i 2.0, MAAS/FFMQ,
 * Gallup Q12, the Edmondson psychological-safety scale - see the source
 * report's Table 16 for the full licensed-instrument list this deliberately
 * avoids). This is an original, unvalidated, educational self-reflection tool -
 * see /leadership/leadership-assessment-validation-roadmap for what turning it
 * into a validated instrument would actually require.
 *
 * The output is prose, never a percentage or an identity label: buildReadout()
 * names specific answered behaviors as a strength or a growth edge, and
 * classifies the care/accountability overlay as a *pattern in today's answers*
 * (using the same quadrant names as the published care/accountability matrix
 * graphic), never as a claim about who the person permanently is.
 */

import {
  DOMAIN_ORDER,
  DOMAIN_LABELS,
  AXIS,
  ITEMS,
  STRENGTH_TEXT,
  GROWTH_TEXT,
  UNIFORM_TEXT,
  LOW_FLOOR_TEXT,
  LOW_FLOOR_THRESHOLD,
  TIE_NOTE_TEXT,
  QUADRANT_TEXT,
  WEAK_SIGNAL_TEXT,
} from './snapshot-items.js';

export const SCHEMA_NAME = 'mbalab.leadership-practice-snapshot';
export const SCHEMA_VERSION = 2;

export const LIMITS = Object.freeze({
  maxSerializedBytes: 20 * 1024,
});

const ITEMS_BY_ID = new Map(ITEMS.map((i) => [i.id, i]));
export const TOTAL_ITEMS = ITEMS.length;

export function createInitialState() {
  return { schema: SCHEMA_NAME, version: SCHEMA_VERSION, savedAt: null, answers: {} };
}

function isValidValue(v) {
  return typeof v === 'number' && Number.isInteger(v) && v >= 1 && v <= 5;
}

export function sanitizeState(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const rawAnswers = r.answers && typeof r.answers === 'object' ? r.answers : {};
  const answers = {};
  for (const item of ITEMS) {
    const v = rawAnswers[item.id];
    if (isValidValue(v)) answers[item.id] = v;
  }
  return {
    schema: SCHEMA_NAME,
    version: SCHEMA_VERSION,
    savedAt: typeof r.savedAt === 'number' ? r.savedAt : null,
    answers,
  };
}

const MIGRATIONS = {
  // No prior schema version has ever shipped - see charter-model.js's identical
  // note. This migrator (and its test) exists to prove the mechanism works
  // before it's ever needed for real, against a synthetic pre-v1 fixture.
  0: (state) => ({ ...state, version: 1 }),
  // v1 -> v2: the item pool was re-derived (D4c) - 15 items across
  // Self-Presence/Relationships-Teams/Org-Systems became 27 items across
  // Self-Awareness/Ego-Patterns/Conflict-Response. Old item ids (sp-1,
  // rt-1, os-1, ...) don't exist in the new ITEMS list, so sanitizeState's
  // per-current-item lookup already drops them naturally on its own - this
  // migrator only needs to bump the version number. A v1 user's old answers
  // don't carry forward, but this tool has no history/streak to lose: it's
  // a single retakeable snapshot, not a multi-day log like the journal.
  1: (state) => ({ ...state, version: 2 }),
};

function migrate(rawState) {
  let state = rawState;
  let migrated = false;
  while (typeof state.version === 'number' && state.version < SCHEMA_VERSION && MIGRATIONS[state.version]) {
    state = MIGRATIONS[state.version](state);
    migrated = true;
  }
  return { state, migrated };
}

/** @returns {{status: 'ok'|'migrated'|'unsupported-newer'|'malformed', state: object|null, raw: string|null}} */
export function parseSavedState(raw) {
  let parsed;
  let rawString;

  if (typeof raw === 'string') {
    rawString = raw;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return { status: 'malformed', state: null, raw: rawString };
    }
  } else {
    parsed = raw;
    try {
      rawString = raw != null ? JSON.stringify(raw) : null;
    } catch {
      rawString = null;
    }
  }

  if (
    !parsed ||
    typeof parsed !== 'object' ||
    parsed.schema !== SCHEMA_NAME ||
    !Number.isInteger(parsed.version) ||
    parsed.version < 0
  ) {
    // Number.isInteger(NaN) is false, so this also rejects a corrupted NaN
    // version - without this, `NaN > SCHEMA_VERSION` and `MIGRATIONS[NaN]`
    // are both silently false/undefined, so a corrupted version would fall
    // through as if it were already current instead of being flagged.
    return { status: 'malformed', state: null, raw: rawString };
  }
  if (parsed.version > SCHEMA_VERSION) {
    return { status: 'unsupported-newer', state: null, raw: rawString };
  }

  const { state: migratedState, migrated } = migrate(parsed);
  return { status: migrated ? 'migrated' : 'ok', state: sanitizeState(migratedState), raw: rawString };
}

export function serializeForSave(state) {
  const json = JSON.stringify(state);
  if (typeof TextEncoder !== 'undefined' && new TextEncoder().encode(json).length > LIMITS.maxSerializedBytes) {
    return null;
  }
  return json;
}

export function setAnswer(state, itemId, value) {
  if (!ITEMS_BY_ID.has(itemId) || !isValidValue(value)) return state;
  return { ...state, answers: { ...state.answers, [itemId]: value } };
}

export function countAnswered(state) {
  return Object.keys(state.answers).length;
}

export function isComplete(state) {
  return ITEMS.every((item) => isValidValue(state.answers[item.id]));
}

function average(values) {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function buildDomainReadout(domain, answers) {
  const items = ITEMS.filter((i) => i.domain === domain);
  const scored = items.map((item) => ({ item, value: answers[item.id] }));

  const strongest = scored.reduce((a, b) => (b.value > a.value ? b : a));
  const weakest = scored.reduce((a, b) => (b.value < a.value ? b : a));
  const uniform = strongest.item.id === weakest.item.id;
  // Even the highest-scoring item in this domain was still genuinely
  // infrequent - see LOW_FLOOR_TEXT's comment for why an affirmative
  // strength claim would be misleading here specifically.
  const lowFloor = !uniform && strongest.value < LOW_FLOOR_THRESHOLD;

  // How many items share the reported high/low value - see TIE_NOTE_TEXT.
  // Counted rather than inferred, so the note can never disagree with the
  // selection it describes.
  const strengthTiedCount = scored.filter((s) => s.value === strongest.value).length;
  const growthTiedCount = scored.filter((s) => s.value === weakest.value).length;
  // Only surfaced for claims actually being shown: a suppressed strength
  // (uniform/lowFloor) has no tie to disclose.
  const strengthTied = !uniform && !lowFloor && strengthTiedCount > 1;
  const growthTied = !uniform && growthTiedCount > 1;
  const tieKind = strengthTied && growthTied ? 'both' : strengthTied ? 'strength' : growthTied ? 'growth' : null;

  return {
    domain,
    label: DOMAIN_LABELS[domain],
    uniform,
    lowFloor,
    strengthItemId: uniform || lowFloor ? null : strongest.item.id,
    strengthText: uniform || lowFloor ? null : STRENGTH_TEXT[strongest.item.id],
    // The growth suggestion stays available even under the low floor - it's
    // still a concrete, useful next step regardless of the domain's overall level.
    growthItemId: uniform ? null : weakest.item.id,
    growthText: uniform ? null : GROWTH_TEXT[weakest.item.id],
    uniformText: uniform ? UNIFORM_TEXT[domain] : null,
    lowFloorText: lowFloor ? LOW_FLOOR_TEXT[domain] : null,
    strengthTied,
    growthTied,
    tieNoteText: tieKind ? TIE_NOTE_TEXT[tieKind] : null,
  };
}

// How close to the scale's own midpoint (3, "Sometimes") both axis averages
// have to be for the classification below to get an honesty caveat - see
// buildOverlay's weakSignal comment for why this exists.
const WEAK_SIGNAL_BAND = 0.5;

function buildOverlay(answers) {
  const careItems = ITEMS.filter((i) => i.axis === AXIS.CARE);
  const acctItems = ITEMS.filter((i) => i.axis === AXIS.ACCOUNTABILITY);
  const careAvg = average(careItems.map((i) => answers[i.id]));
  const acctAvg = average(acctItems.map((i) => answers[i.id]));
  // Strictly greater than 3 ("Sometimes"), not >= - the scale's own
  // definitional midpoint must never read as "high" on either axis. With
  // >=, a fully neutral all-3s profile (genuinely uncommitted on both
  // axes) was awarded "Compassionate Accountability" - the framework's own
  // stated *target* quadrant - while the domain readouts on that exact same
  // input correctly said "there's no single standout item to highlight
  // yet." A tied-at-the-midpoint average is not evidence of the target
  // pattern; it's evidence of no clear pattern either way.
  const careHigh = careAvg > 3;
  const acctHigh = acctAvg > 3;
  const quadrantKey = careHigh
    ? (acctHigh ? 'compassionate-accountability' : 'protective-indulgence')
    : (acctHigh ? 'coercive-pressure' : 'neglect');

  // The classification above is a hard binary split on both axes, so a
  // genuinely neutral profile - every axis-tagged item answered "Sometimes"
  // - has to land somewhere, and with no fifth "no clear pattern" quadrant
  // available it lands on 'neglect', the harshest-sounding label, purely
  // because >3 was the chosen cutoff rather than >=3. That's not wrong the
  // way the >=3 bug above was (the classification itself is still doing
  // exactly what a fixed 2x2 split can do), but a profile with almost no
  // real lean on either axis reading as a confident "problems drift until
  // unavoidable" overstates what weak, near-midpoint answers actually show.
  // Rather than add a fifth quadrant (this matches a real, external,
  // already-published matrix graphic with exactly four), this adds the same
  // kind of honest caveat the domain-level readouts already use for their
  // own low-signal cases (see UNIFORM_TEXT/LOW_FLOOR_TEXT above) - the
  // classification itself is unchanged.
  const weakSignal = Math.abs(careAvg - 3) <= WEAK_SIGNAL_BAND && Math.abs(acctAvg - 3) <= WEAK_SIGNAL_BAND;

  return {
    careAvg,
    acctAvg,
    quadrantKey,
    weakSignal,
    weakSignalText: weakSignal ? WEAK_SIGNAL_TEXT : null,
    ...QUADRANT_TEXT[quadrantKey],
  };
}

/** @returns {null | {domains: object[], overlay: object}} null until every item is answered. */
export function buildReadout(state) {
  if (!isComplete(state)) return null;
  return {
    domains: DOMAIN_ORDER.map((domain) => buildDomainReadout(domain, state.answers)),
    overlay: buildOverlay(state.answers),
  };
}
